import React, { useState } from 'react';
import logo from './asset/logo.png'
import { Shield, FileText, Lock, User, Database, Mail, Menu, X, ChevronDown, ChevronUp, CheckCircle, AlertCircle, Eye, Download, Trash2, Edit } from 'lucide-react';

const PrivacyPolicy = () => {
  const [expandedSection, setExpandedSection] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      {/* Navbar */}
      <nav className="bg-black/80 backdrop-blur-md shadow-lg sticky top-0 z-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <div className="flex items-center">
              <div className="flex items-center gap-2">
               <img
                   src={logo} // replace with your image path
                   alt="Description"
                   width="100px"
                 />
              </div>
            </div>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center gap-8">
              <a href="/" className="text-gray-700 hover:text-blue-600 transition-colors font-medium">
                Home
              </a>
             
            </div>

            {/* Mobile Menu Button */}
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden text-gray-700"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 space-y-3 border-t border-gray-200">
              <a href="#" className="block text-gray-700 hover:text-blue-600 transition-colors font-medium">
                Home
              </a>
          
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 opacity-90"></div>
        <div className="relative py-16 px-4 text-white">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
              <Shield className="w-5 h-5" />
              <span className="text-sm font-semibold">Your Privacy, Our Priority</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Privacy Policy
            </h1>
            <p className="text-xl text-white/90 max-w-2xl mx-auto">
              Comprehensive information about how we collect, use, and protect your personal data
            </p>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-4 text-sm text-white/80">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4" />
                <span>Last Updated: January 1, 2025</span>
              </div>
              <div className="flex items-center gap-2">
                <Lock className="w-4 h-4" />
                <span>Compliant with IT Act, 2000</span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Decorative Elements */}
        <div className="absolute top-10 left-10 w-20 h-20 bg-white/10 rounded-full blur-xl"></div>
        <div className="absolute bottom-10 right-10 w-32 h-32 bg-white/10 rounded-full blur-2xl"></div>
      </div>

      {/* Quick Stats */}
      <div className="max-w-4xl mx-auto px-4 -mt-8 mb-8 relative z-10">
        <div className="grid md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl shadow-lg p-4 text-center border border-gray-200">
            <Shield className="w-8 h-8 text-green-600 mx-auto mb-2" />
            <p className="font-bold text-gray-900">Secure</p>
            <p className="text-xs text-gray-600">256-bit encryption</p>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-4 text-center border border-gray-200">
            <Lock className="w-8 h-8 text-blue-600 mx-auto mb-2" />
            <p className="font-bold text-gray-900">Protected</p>
            <p className="text-xs text-gray-600">Your data is safe</p>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-4 text-center border border-gray-200">
            <User className="w-8 h-8 text-purple-600 mx-auto mb-2" />
            <p className="font-bold text-gray-900">Your Rights</p>
            <p className="text-xs text-gray-600">Full control</p>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-4 text-center border border-gray-200">
            <FileText className="w-8 h-8 text-indigo-600 mx-auto mb-2" />
            <p className="font-bold text-gray-900">Transparent</p>
            <p className="text-xs text-gray-600">Clear policies</p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 pb-12">
        {/* Introduction */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-6 border border-gray-100">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <FileText className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">Introduction</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                IEEE Paper Pro ("we," "our," or "us") operates the IEEE Paper Pro platform 
                (the "Service"). This Privacy Policy explains how we collect, use, disclose, 
                and safeguard your information when you use our Service.
              </p>
              <p className="text-gray-700 leading-relaxed">
                We are committed to protecting your privacy and complying with applicable data 
                protection laws in India, including the Information Technology Act, 2000 and 
                the Information Technology (Reasonable Security Practices and Procedures and 
                Sensitive Personal Data or Information) Rules, 2011.
              </p>
            </div>
          </div>
        </div>

        {/* Information We Collect */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200 hover:shadow-xl transition-shadow duration-300 mb-4">
          <button
            onClick={() => toggleSection(2)}
            className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                <Database className="w-5 h-5 text-indigo-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">Information We Collect</h2>
            </div>
            {expandedSection === 2 ? (
              <ChevronUp className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </button>
          {expandedSection === 2 && (
            <div className="px-6 pb-6 border-t border-gray-100 pt-6 animate-fadeIn">
              <div className="space-y-6">
                <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                  <h3 className="font-bold text-blue-900 mb-3 flex items-center gap-2">
                    <User className="w-5 h-5" />
                    Personal Information
                  </h3>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2 text-blue-800">
                      <CheckCircle className="w-4 h-4 mt-1 flex-shrink-0" />
                      <span>Name and contact information (email address)</span>
                    </li>
                    <li className="flex items-start gap-2 text-blue-800">
                      <CheckCircle className="w-4 h-4 mt-1 flex-shrink-0" />
                      <span>Academic and professional details</span>
                    </li>
                    <li className="flex items-start gap-2 text-blue-800">
                      <CheckCircle className="w-4 h-4 mt-1 flex-shrink-0" />
                      <span>Payment information (processed securely through our payment partners)</span>
                    </li>
                    <li className="flex items-start gap-2 text-blue-800">
                      <CheckCircle className="w-4 h-4 mt-1 flex-shrink-0" />
                      <span>User account credentials</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                  <h3 className="font-bold text-purple-900 mb-3 flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    Academic Content
                  </h3>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2 text-purple-800">
                      <CheckCircle className="w-4 h-4 mt-1 flex-shrink-0" />
                      <span>Research papers, abstracts, and academic content you create</span>
                    </li>
                    <li className="flex items-start gap-2 text-purple-800">
                      <CheckCircle className="w-4 h-4 mt-1 flex-shrink-0" />
                      <span>Citations and references</span>
                    </li>
                    <li className="flex items-start gap-2 text-purple-800">
                      <CheckCircle className="w-4 h-4 mt-1 flex-shrink-0" />
                      <span>Tables, images, and figures uploaded</span>
                    </li>
                    <li className="flex items-start gap-2 text-purple-800">
                      <CheckCircle className="w-4 h-4 mt-1 flex-shrink-0" />
                      <span>Document metadata and formatting preferences</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                  <h3 className="font-bold text-green-900 mb-3 flex items-center gap-2">
                    <Database className="w-5 h-5" />
                    Technical Information
                  </h3>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2 text-green-800">
                      <CheckCircle className="w-4 h-4 mt-1 flex-shrink-0" />
                      <span>IP address and device information</span>
                    </li>
                    <li className="flex items-start gap-2 text-green-800">
                      <CheckCircle className="w-4 h-4 mt-1 flex-shrink-0" />
                      <span>Browser type and version</span>
                    </li>
                    <li className="flex items-start gap-2 text-green-800">
                      <CheckCircle className="w-4 h-4 mt-1 flex-shrink-0" />
                      <span>Usage patterns and service analytics</span>
                    </li>
                    <li className="flex items-start gap-2 text-green-800">
                      <CheckCircle className="w-4 h-4 mt-1 flex-shrink-0" />
                      <span>Cookies and similar tracking technologies</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* How We Use Your Information */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200 hover:shadow-xl transition-shadow duration-300 mb-4">
          <button
            onClick={() => toggleSection(3)}
            className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">How We Use Your Information</h2>
            </div>
            {expandedSection === 3 ? (
              <ChevronUp className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </button>
          {expandedSection === 3 && (
            <div className="px-6 pb-6 border-t border-gray-100 pt-6 animate-fadeIn">
              <div className="grid md:grid-cols-2 gap-3">
                <div className="flex items-start gap-2 p-3 bg-green-50 rounded-lg border border-green-200">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Provide and maintain our Service</span>
                </div>
                <div className="flex items-start gap-2 p-3 bg-green-50 rounded-lg border border-green-200">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Process payments and manage subscriptions</span>
                </div>
                <div className="flex items-start gap-2 p-3 bg-green-50 rounded-lg border border-green-200">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Improve and personalize user experience</span>
                </div>
                <div className="flex items-start gap-2 p-3 bg-green-50 rounded-lg border border-green-200">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Develop new features and services</span>
                </div>
                <div className="flex items-start gap-2 p-3 bg-green-50 rounded-lg border border-green-200">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Communicate service updates</span>
                </div>
                <div className="flex items-start gap-2 p-3 bg-green-50 rounded-lg border border-green-200">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Ensure Terms of Service compliance</span>
                </div>
                <div className="flex items-start gap-2 p-3 bg-green-50 rounded-lg border border-green-200 md:col-span-2">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Protect against fraud and unauthorized access</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Data Ownership - Highlighted */}
        <div className="bg-gradient-to-br from-blue-600 to-indigo-600 rounded-2xl shadow-2xl p-8 mb-6 text-white">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold mb-3">Data Ownership and Intellectual Property</h2>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 mb-4 border border-white/20">
                <h3 className="font-bold text-white mb-2 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5" />
                  Your Content Rights
                </h3>
                <p className="text-white/90 leading-relaxed">
                  You retain all intellectual property rights to the academic content you create 
                  using our Service. We do not claim ownership over your research papers, 
                  academic work, or any original content you generate.
                </p>
              </div>
              <p className="text-white/90 leading-relaxed">
                However, by using our Service, you grant us a limited license to store, 
                process, and display your content solely for the purpose of providing the 
                Service to you.
              </p>
            </div>
          </div>
        </div>

        {/* Data Security */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-6 border border-gray-100">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <Lock className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">Data Security</h2>
              <p className="text-gray-700 leading-relaxed">
                We implement appropriate technical and organizational security measures 
                to protect your data, including encryption, access controls, and regular 
                security assessments.
              </p>
              <div className="grid md:grid-cols-3 gap-3 mt-4">
                <div className="p-3 bg-red-50 rounded-lg border border-red-200 text-center">
                  <Lock className="w-6 h-6 text-red-600 mx-auto mb-1" />
                  <p className="text-sm font-semibold text-red-900">256-bit Encryption</p>
                </div>
                <div className="p-3 bg-red-50 rounded-lg border border-red-200 text-center">
                  <Shield className="w-6 h-6 text-red-600 mx-auto mb-1" />
                  <p className="text-sm font-semibold text-red-900">Access Controls</p>
                </div>
                <div className="p-3 bg-red-50 rounded-lg border border-red-200 text-center">
                  <AlertCircle className="w-6 h-6 text-red-600 mx-auto mb-1" />
                  <p className="text-sm font-semibold text-red-900">Regular Audits</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Your Rights */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200 hover:shadow-xl transition-shadow duration-300 mb-6">
          <button
            onClick={() => toggleSection(8)}
            className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <User className="w-5 h-5 text-purple-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">Your Rights Under Indian Law</h2>
            </div>
            {expandedSection === 8 ? (
              <ChevronUp className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </button>
          {expandedSection === 8 && (
            <div className="px-6 pb-6 border-t border-gray-100 pt-6 animate-fadeIn">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Eye className="w-5 h-5 text-purple-600" />
                    <h3 className="font-bold text-purple-900">Access</h3>
                  </div>
                  <p className="text-sm text-purple-800">View your personal information</p>
                </div>
                <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Edit className="w-5 h-5 text-purple-600" />
                    <h3 className="font-bold text-purple-900">Correction</h3>
                  </div>
                  <p className="text-sm text-purple-800">Update inaccurate data</p>
                </div>
                <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Trash2 className="w-5 h-5 text-purple-600" />
                    <h3 className="font-bold text-purple-900">Deletion</h3>
                  </div>
                  <p className="text-sm text-purple-800">Request data removal</p>
                </div>
                <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Download className="w-5 h-5 text-purple-600" />
                    <h3 className="font-bold text-purple-900">Portability</h3>
                  </div>
                  <p className="text-sm text-purple-800">Export your data</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Contact Card */}
        <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 rounded-2xl p-8 text-white shadow-2xl">
          <div className="text-center">
            <Mail className="w-12 h-12 mx-auto mb-4" />
            <h3 className="text-2xl font-bold mb-2">Privacy Questions?</h3>
            <p className="mb-6 text-white/90">
              Contact our privacy team to exercise your rights or for any privacy-related inquiries
            </p>
            <div className="space-y-2">
              <a
                href="mailto:privacy@ieeepaperpro.com"
                className="inline-block bg-white text-indigo-600 px-8 py-3 rounded-full font-bold hover:bg-gray-100 transition-all duration-300 shadow-lg"
              >
                support@ieeeassistant.in
              </a>
              <p className="text-sm text-white/80">
                Nagpur, Maharashtra, India
              </p>
            </div>
          </div>
        </div>

        {/* Jurisdiction Notice */}
        <div className="mt-6 bg-amber-50 border-l-4 border-amber-500 rounded-lg p-6">
          <div className="flex gap-3">
            <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0" />
            <div>
              <strong className="text-amber-900 block mb-2">Governing Law</strong>
              <p className="text-sm text-amber-800">
                This Privacy Policy is governed by the laws of India. Any disputes shall 
                be subject to the exclusive jurisdiction of the courts in Nagpur, Maharashtra.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8 mt-16">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-gray-400">
            © 2025 IEEE Paper Pro. All rights reserved. | Compliant with IT Act, 2000
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;